import React, { createContext, useState, useContext, useEffect } from 'react';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [token, setToken] = useState(null);
  const [role, setRole] = useState(null); 
  const [currency, setCurrency] = useState('uah'); 
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // ❗️❗️❗️ ИЗМЕНЕНА: Регистрация (больше не логинит) ❗️❗️❗️
  const register = async (email, password, refCode) => { 
    const response = await fetch('/api/public/register', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password, ref_code: refCode }), 
    });
    
    const data = await response.json();
    if (!response.ok) {
      const errorMsg = data.errors ? data.errors[0].message : data.message;
      throw new Error(errorMsg || 'Ошибка регистрации');
    }
    
    // ❗️ УСПЕХ: Просто возвращаем сообщение
    return data; // (Вернет { message: "Регистрация успешна. Пожалуйста..." })
  };

  // ❗️ ИЗМЕНЕНА: Вход (ловит 'NOT_VERIFIED')
  const login = async (email, password) => {
    const response = await fetch('/api/public/login', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password }),
    });

    const data = await response.json();
    
    // ❗️ Новая обработка ошибок
    if (!response.ok) {
        // Если код 403 и "NOT_VERIFIED", кидаем особую ошибку
        if (response.status === 403 && data.code === "NOT_VERIFIED") {
            throw new Error("NOT_VERIFIED");
        }
        throw new Error(data.message || 'Ошибка входа');
    }

    const newToken = data.token;
    const newRole = data.role;
    
    if (newToken && newRole) {
      setToken(newToken);
      setRole(newRole); 
      localStorage.setItem('userToken', newToken);
      localStorage.setItem('userRole', newRole); 
      
      // Загружаем данные пользователя сразу после обычного логина
      try {
        const userResponse = await fetch('/api/client/me?force_refresh=true', {
          headers: { 'Authorization': `Bearer ${newToken}` }
        });
        if (userResponse.ok) {
          const userData = await userResponse.json();
          const userResponseData = userData.response || userData;
          if (userResponseData) {
            setUser(userResponseData);
            if (userResponseData.preferred_currency) {
              setCurrency(userResponseData.preferred_currency);
              localStorage.setItem('userCurrency', userResponseData.preferred_currency);
            }
          }
        }
      } catch (e) {
        console.error('Ошибка загрузки данных пользователя после логина:', e);
        // Не критично, данные загрузятся при следующем запросе
      }
      
      return { token: newToken, role: newRole };
    } else {
      throw new Error('Токен или роль не были получены');
    }
  };

  // Авторизация через Telegram
  const telegramLogin = async (telegramData) => {
    const response = await fetch('/api/public/telegram-login', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(telegramData),
    });

    const data = await response.json();
    
    if (!response.ok) {
      throw new Error(data.message || 'Ошибка входа через Telegram');
    }

    const newToken = data.token;
    const newRole = data.role;
    
    if (newToken && newRole) {
      // Сначала сохраняем токен и роль
      setToken(newToken);
      setRole(newRole); 
      localStorage.setItem('userToken', newToken);
      localStorage.setItem('userRole', newRole); 
      
      // Загружаем данные пользователя СРАЗУ после авторизации через Telegram
      // Ждем завершения загрузки перед возвратом
      try {
        // Даем небольшую задержку, чтобы токен точно был установлен
        await new Promise(resolve => setTimeout(resolve, 100));
        
        const userResponse = await fetch('/api/client/me?force_refresh=true', {
          headers: { 'Authorization': `Bearer ${newToken}` }
        });
        if (userResponse.ok) {
          const userData = await userResponse.json();
          const userResponseData = userData.response || userData;
          if (userResponseData) {
            // Устанавливаем данные пользователя
            setUser(userResponseData);
            
            // Также устанавливаем валюту из данных пользователя, если есть
            if (userResponseData.preferred_currency) {
              setCurrency(userResponseData.preferred_currency);
              localStorage.setItem('userCurrency', userResponseData.preferred_currency);
            }
            
            // Возвращаем данные с информацией о пользователе
            return { token: newToken, role: newRole, user: userResponseData };
          }
        } else if (userResponse.status === 401) {
          // Если получили 401, возможно токен еще не активирован, пробуем подождать немного
          console.warn('Получен 401 при загрузке данных пользователя, повторная попытка через 300ms...');
          
          // Повторная попытка через небольшую задержку
          await new Promise(resolve => setTimeout(resolve, 300));
          
          const retryResponse = await fetch('/api/client/me?force_refresh=true', {
            headers: { 'Authorization': `Bearer ${newToken}` }
          });
          
          if (retryResponse.ok) {
            const retryData = await retryResponse.json();
            const retryUserData = retryData.response || retryData;
            if (retryUserData) {
              setUser(retryUserData);
              if (retryUserData.preferred_currency) {
                setCurrency(retryUserData.preferred_currency);
                localStorage.setItem('userCurrency', retryUserData.preferred_currency);
              }
              return { token: newToken, role: newRole, user: retryUserData };
            }
          }
        }
      } catch (e) {
        console.error('Ошибка загрузки данных пользователя после Telegram логина:', e);
        // Не критично, данные загрузятся при следующем запросе
      }
      
      return { token: newToken, role: newRole };
    } else {
      throw new Error('Токен или роль не были получены');
    }
  };

  // --- (logout, useEffect - БЕЗ ИЗМЕНЕНИЙ) ---
  const logout = () => {
    setToken(null);
    setRole(null);
    setUser(null);
    setCurrency('uah');
    localStorage.removeItem('userToken');
    localStorage.removeItem('userRole');
    localStorage.removeItem('userCurrency');
    localStorage.removeItem('userLang');
  };

  useEffect(() => {
    const storedToken = localStorage.getItem('userToken');
    const storedRole = localStorage.getItem('userRole'); 
    const storedCurrency = localStorage.getItem('userCurrency'); 
    if (storedToken && storedRole) {
      setToken(storedToken);
      setRole(storedRole); 
      
      // Загружаем данные пользователя сразу при инициализации, если токен есть
      // Это особенно важно после Telegram логина, когда страница перезагружается
      const fetchInitialUserData = async () => {
        try {
          const userResponse = await fetch('/api/client/me?force_refresh=true', {
            headers: { 'Authorization': `Bearer ${storedToken}` }
          });
          if (userResponse.ok) {
            const userData = await userResponse.json();
            const userResponseData = userData.response || userData;
            if (userResponseData) {
              setUser(userResponseData);
              // Приоритет: localStorage (если пользователь явно выбрал) > данные из БД
              // Если в localStorage есть валюта, используем её и обновляем в БД
              if (storedCurrency) {
                setCurrency(storedCurrency);
                // Синхронизируем с БД, если она отличается
                if (userResponseData.preferred_currency !== storedCurrency) {
                  try {
                    await fetch('/api/client/settings', {
                      method: 'POST',
                      headers: {
                        'Authorization': `Bearer ${storedToken}`,
                        'Content-Type': 'application/json'
                      },
                      body: JSON.stringify({ preferred_currency: storedCurrency })
                    });
                  } catch (e) {
                    console.error('Ошибка синхронизации валюты:', e);
                  }
                }
              } else if (userResponseData.preferred_currency) {
                // Если в localStorage нет, используем из БД
                setCurrency(userResponseData.preferred_currency);
                localStorage.setItem('userCurrency', userResponseData.preferred_currency);
              }
            }
          }
        } catch (e) {
          console.error('Ошибка загрузки данных пользователя при инициализации:', e);
          // Если ошибка, используем валюту из localStorage
          if (storedCurrency) {
            setCurrency(storedCurrency);
          }
        } finally {
          setLoading(false);
        }
      };
      
      fetchInitialUserData();
    } else {
      setLoading(false);
      // Если нет токена, используем валюту из localStorage
      if (storedCurrency) {
        setCurrency(storedCurrency);
      }
    }
  }, []);

  const value = {
    token, role, user, setUser,
    setToken, setRole, // Добавляем для автоматической авторизации
    login, logout, register, telegramLogin,
    loading, currency, setCurrency,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  return useContext(AuthContext);
};